/* eslint-disable brace-style */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { take, takeUntil } from 'rxjs/operators';
import { Params } from '@angular/router';
import { ErrorObject } from 'src/app/_models/error';
import { Experiment } from 'src/app/_models/experiment';
import { FormBase } from 'src/app/_models/form-base';
import { ExperimentService } from 'src/app/_services/experiment.service';
import { IconNames } from 'src/app/_components/icons/icon-names';
import { ExperimentUpdate } from 'src/app/_models/experiment-update';
import { ChangelogResponse } from 'src/app/_models/api-responses';
import {
  ExperimentMode,
  ExperimentModeDisplay,
} from 'src/app/_models/experiment-mode';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { stepValidator } from 'src/app/_helpers/step-validator.validator';
import { integerValidator } from 'src/app/_helpers/integer-validator.validator';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { FormatRolePipe } from 'src/app/_helpers/format-role.pipe';
import { ModalComponent } from '../../_components/modals/modal.component';
import { UnderlinedDropdownFormFieldComponent } from '../../_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { SmallLoadingSpinnerComponent } from 'src/app/_components/small-loading-spinner/small-loading-spinner.component';
import { DropdownFormFieldComponent } from '../../_components/forms/dropdown-form-field/dropdown-form-field.component';
import { MatIcon } from '@angular/material/icon';
import { UnderlinedTextareaFieldComponent } from '../../_components/forms/underlined-textarea-field/underlined-textarea-field.component';
import { RadioButtonComponent } from '../../_components/selection-controls/radio-button/radio-button.component';
import { SlideToggleComponent } from '../../_components/selection-controls/slide-toggle/slide-toggle.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { StatusIndicatorComponent } from '../../_components/chips/status-indicator/status-indicator.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { DatePipe } from '@angular/common';
import { OptionTabToggleComponent } from '../../_components/selection-controls/option-tab-toggle/option-tab-toggle.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { RangeValidator } from 'src/app/_helpers/range.validator';
import { SpargingStatus } from 'src/app/_models/sparging-status';
import { ExperimentStatus } from 'src/app/_models/experiment-status';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { DataService } from 'src/app/_services/data.service';
import {
  convertBooleanToSpecGain,
  convertInputTypeToBoolean,
  Flask,
  getSpargingStatus,
} from 'src/app/_helpers/experiment-utils';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { NumberFormFieldComponent } from 'src/app/_components/forms/number-form-field/number-form-field.component';
import { ExperimentParameterRanges } from 'src/app/_helpers/experiment-min-max';
import { FluorescenceChannelCardComponent } from 'src/app/_components/cards/fluorescence-channel-card/fluorescence-channel-card.component';
import { MatOutlinedButtonComponent } from 'src/app/_components/buttons/mat-outlined-button/mat-outlined-button.component';
import { UnderlinedTextButtonComponent } from 'src/app/_components/buttons/underlined-text-button/underlined-text-button.component';
import { MatTableModule } from '@angular/material/table';
import { Device } from 'src/app/_models/device';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';
import { TabOptions } from 'src/app/_models/tab-options';
import { User } from 'src/app/_models/user';
import { ExperimentFormBase } from '../_base/experiment-form.base';

@Component({
  selector: 'app-control-experiment',
  templateUrl: './control-experiment.component.html',
  styleUrls: ['./control-experiment.component.scss'],
  imports: [
    OptionTabToggleComponent,
    StatusIndicatorComponent,
    IconButtonComponent,
    ButtonComponent,
    UnderlinedFormFieldComponent,
    SlideToggleComponent,
    RadioButtonComponent,
    UnderlinedTextareaFieldComponent,
    MatIcon,
    DropdownFormFieldComponent,
    ValidationModalComponent,
    ModalComponent,
    DatePipe,
    RoleCheckPipe,
    ErrorPageComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    NumberFormFieldComponent,
    FluorescenceChannelCardComponent,
    MatOutlinedButtonComponent,
    UnderlinedTextButtonComponent,
    MatIconButtonComponent,
    MatTableModule,
    UnderlinedDropdownFormFieldComponent,
    FormatRolePipe,
    SmallLoadingSpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ControlExperimentComponent
  extends ExperimentFormBase
  implements OnInit, OnDestroy
{
  // ── Unique injected services ───────────────────────────────────────────────
  protected experimentService = inject(ExperimentService);
  protected authenticationService = inject(AuthenticationService);
  protected dataService = inject(DataService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild(MatAccordion) accordion: MatAccordion;

  // ── Abstract member implementations ───────────────────────────────────────
  readonly experimentId = signal<number | null>(null);
  override readonly entityId = this.experimentId;

  override fetchChangelog(): void {
    this.getExperimentChangelog();
  }

  override getSavedEntity(): Experiment | null {
    return this.experiment ?? null;
  }

  // ── Routing ────────────────────────────────────────────────────────────────
  navOptions: TabOptions[];

  // ── Experiment data ────────────────────────────────────────────────────────
  experiment: Experiment;
  ExperimentStatus = ExperimentStatus;
  ExperimentModeDisplay = ExperimentModeDisplay;
  ExperimentParameterRanges = ExperimentParameterRanges;
  minNoteLength = NotesParameterRanges.EXPERIMENT_NOTES.minNoteLength;
  maxNoteLength = NotesParameterRanges.EXPERIMENT_NOTES.maxNoteLength;

  // ── Device controls ────────────────────────────────────────────────────────
  devices: Device[] = [];

  // OD controls
  flasks = ['OD_A', 'OD_B', 'OD_C', 'OD_D'];
  renderODGraph = false;
  selectedODFlasks: Array<string> = [];

  // Temperature controls
  temperatureFlasks = ['TFlask_A', 'TFlask_B', 'TFlask_C', 'TFlask_D'];
  renderTemperatureGraph = false;
  selectedTemperatureFlasks: Array<string> = [];

  // Oxygen controls
  oxygenFlasks = ['A', 'B', 'C', 'D'];
  renderOxygenGraph = false;
  selectedOxygenFlasks: Array<string> = [];

  // pH controls
  flasksPH = ['PHA', 'PHB', 'PHC', 'PHD'];
  renderPHGraph = false;
  selectedPHFlasks: Array<string> = [];

  // Fluorescence controls
  renderFluorescenceGraph = false;
  isFetchingFluorescenceData = false;
  selectedFluorescenceFlasks: Array<string> = [];
  fluorescenceFlasks = ['A', 'B', 'C', 'D'];

  // ── Control signals ────────────────────────────────────────────────────────
  error = signal<ErrorObject | null>(null);
  changelogError = signal<ErrorObject | null>(null);
  getExperimentError = signal<ErrorObject | null>(null);
  deleteError = signal<ErrorObject | null>(null);
  deleteIsProcessing = signal<boolean>(false);
  loading = signal<boolean>(false);
  SpargingStatus = SpargingStatus;

  stopExperimentProcessing = signal<boolean>(false);
  updateExperimentProcessing = signal<boolean>(false);
  pauseDevicesProcessing = signal<boolean>(false);

  updateSpinner = IconNames.Sync;

  pauseDeviceTitleText = signal<string>('');
  pauseDeviceValidationText = signal<string>('');
  pauseDeviceButtonText = signal<string>('');

  // ── Experiment update ──────────────────────────────────────────────────────
  stopExperiment: ExperimentUpdate;
  updateExperiment: ExperimentUpdate;
  experimentDataLastUpdated = signal<Date>(new Date());
  unsavedCachedData = signal<ExperimentUpdate | null>(null);

  // ── Parameter ranges unique to control experiment ──────────────────────────
  displayTargetOD = true;
  protected override minCultureTemp =
    ExperimentParameterRanges.CULTURE_TEMPERATURE.min;

  noteField = new FormBase({
    key: 'noteField',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Enter notes here...',
    disabled: true,
    required: false,
    value: '',
    options: [],
  });

  // ── Experiment update form ─────────────────────────────────────────────────
  // Disable fields by default; enableFormControls() enables based on
  // user role, experiment status, and module configuration.
  experimentUpdateForm = this.formBuilder.group({
    [this.enableNotificationsField.key]: [this.enableNotificationsField.value, [Validators.required]],
    [this.notificationEmailField.key]: [{ value: this.notificationEmailField.value, disabled: true }],
    [this.experimentNameField.key]: [
      { value: '', disabled: this.experimentNameField.disabled },
    ],
    [this.organismField.key]: [
      { value: '', disabled: this.organismField.disabled },
      [Validators.minLength(2), Validators.maxLength(100)],
    ],
    [this.strainField.key]: [
      { value: '', disabled: this.strainField.disabled },
      [Validators.minLength(2), Validators.maxLength(100)],
    ],
    [this.mediaField.key]: [
      { value: '', disabled: this.mediaField.disabled },
      [Validators.minLength(2), Validators.maxLength(100)],
    ],
    [this.samplingTimeField.key]: [
      {
        value: this.samplingTimeField.value,
        disabled: this.samplingTimeField.disabled,
      },
      [
        Validators.min(this.minSampleTimeInMins),
        Validators.max(this.maxSampleTimeInMins),
        stepValidator(this.sampleTimeStep),
        integerValidator(),
      ],
    ],
    [this.experimentTimeField.key]: [
      this.experimentTimeField.value,
      [
        Validators.min(this.minExperimentTimeInHours),
        Validators.max(this.maxExperimentTimeInHours),
        stepValidator(this.experimentTimeStep),
        integerValidator(),
      ],
    ],
    [this.speedAField.key]: [
      { value: this.speedAField.value, disabled: this.speedAField.disabled },
      [
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ],
    ],
    [this.speedBField.key]: [
      { value: this.speedBField.value, disabled: this.speedBField.disabled },
      [
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ],
    ],
    [this.speedCField.key]: [
      { value: this.speedCField.value, disabled: this.speedCField.disabled },
      [
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ],
    ],
    [this.speedDField.key]: [
      { value: this.speedDField.value, disabled: this.speedDField.disabled },
      [
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ],
    ],
    [this.setStirringSpeedAField.key]: [
      {
        value: this.setStirringSpeedAField.value,
        disabled: this.setStirringSpeedAField.disabled,
      },
    ],
    [this.setStirringSpeedBField.key]: [
      {
        value: this.setStirringSpeedBField.value,
        disabled: this.setStirringSpeedBField.disabled,
      },
    ],
    [this.setStirringSpeedCField.key]: [
      {
        value: this.setStirringSpeedCField.value,
        disabled: this.setStirringSpeedCField.disabled,
      },
    ],
    [this.setStirringSpeedDField.key]: [
      {
        value: this.setStirringSpeedDField.value,
        disabled: this.setStirringSpeedDField.disabled,
      },
    ],
    [this.ambientTemperatureField.key]: [
      { value: '', disabled: true },
    ],
    [this.temperatureControlAField.key]: [{ value: '', disabled: true }],
    [this.temperatureControlBField.key]: [{ value: '', disabled: true }],
    [this.temperatureControlCField.key]: [{ value: '', disabled: true }],
    [this.temperatureControlDField.key]: [{ value: '', disabled: true }],
    [this.temperatureAField.key]: [
      {
        value: this.temperatureAField.value,
        disabled: this.temperatureAField.disabled,
      },
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureBField.key]: [
      {
        value: this.temperatureBField.value,
        disabled: this.temperatureBField.disabled,
      },
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureCField.key]: [
      {
        value: this.temperatureCField.value,
        disabled: this.temperatureCField.disabled,
      },
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureDField.key]: [
      {
        value: this.temperatureDField.value,
        disabled: this.temperatureDField.disabled,
      },
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    // Turbidostat
    [this.maintainODField.key]: [
      {
        value: this.maintainODField.value,
        disabled: this.maintainODField.disabled,
      },
    ],
    [this.targetODAField.key]: [
      {
        value: this.targetODAField.value,
        disabled: this.targetODAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ]),
    ],
    [this.targetODBField.key]: [
      {
        value: this.targetODBField.value,
        disabled: this.targetODBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ]),
    ],
    [this.targetODCField.key]: [
      {
        value: this.targetODCField.value,
        disabled: this.targetODCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ]),
    ],
    [this.targetODDField.key]: [
      {
        value: this.targetODDField.value,
        disabled: this.targetODDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ]),
    ],
    [this.turbAActiveField.key]: [
      {
        value: this.turbAActiveField.value,
        disabled: this.turbAActiveField.disabled,
      },
    ],
    [this.turbBActiveField.key]: [
      {
        value: this.turbBActiveField.value,
        disabled: this.turbBActiveField.disabled,
      },
    ],
    [this.turbCActiveField.key]: [
      {
        value: this.turbCActiveField.value,
        disabled: this.turbCActiveField.disabled,
      },
    ],
    [this.turbDActiveField.key]: [
      {
        value: this.turbDActiveField.value,
        disabled: this.turbDActiveField.disabled,
      },
    ],
    // Chemostat
    [this.chemoAActiveField.key]: [
      {
        value: this.chemoAActiveField.value,
        disabled: this.chemoAActiveField.disabled,
      },
    ],
    [this.chemoBActiveField.key]: [
      {
        value: this.chemoBActiveField.value,
        disabled: this.chemoBActiveField.disabled,
      },
    ],
    [this.chemoCActiveField.key]: [
      {
        value: this.chemoCActiveField.value,
        disabled: this.chemoCActiveField.disabled,
      },
    ],
    [this.chemoDActiveField.key]: [
      {
        value: this.chemoDActiveField.value,
        disabled: this.chemoDActiveField.disabled,
      },
    ],
    [this.flowRateAField.key]: [
      {
        value: this.flowRateAField.value,
        disabled: this.flowRateAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateBField.key]: [
      {
        value: this.flowRateBField.value,
        disabled: this.flowRateBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateCField.key]: [
      {
        value: this.flowRateCField.value,
        disabled: this.flowRateCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateDField.key]: [
      {
        value: this.flowRateDField.value,
        disabled: this.flowRateDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Morbidostat controls
    [this.morbidostatAActiveField.key]: [
      {
        value: this.morbidostatAActiveField.value,
        disabled: this.morbidostatAActiveField.disabled,
      },
    ],
    [this.morbidostatBActiveField.key]: [
      {
        value: this.morbidostatBActiveField.value,
        disabled: this.morbidostatBActiveField.disabled,
      },
    ],
    [this.morbidostatCActiveField.key]: [
      {
        value: this.morbidostatCActiveField.value,
        disabled: this.morbidostatCActiveField.disabled,
      },
    ],
    [this.morbidostatDActiveField.key]: [
      {
        value: this.morbidostatDActiveField.value,
        disabled: this.morbidostatDActiveField.disabled,
      },
    ],
    [this.thresholdODAField.key]: [
      {
        value: this.thresholdODAField.value,
        disabled: this.thresholdODAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODBField.key]: [
      {
        value: this.thresholdODBField.value,
        disabled: this.thresholdODBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODCField.key]: [
      {
        value: this.thresholdODCField.value,
        disabled: this.thresholdODCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODDField.key]: [
      {
        value: this.thresholdODDField.value,
        disabled: this.thresholdODDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.dilutionPeriodAField.key]: [
      {
        value: this.dilutionPeriodAField.value,
        disabled: this.dilutionPeriodAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodBField.key]: [
      {
        value: this.dilutionPeriodBField.value,
        disabled: this.dilutionPeriodBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodCField.key]: [
      {
        value: this.dilutionPeriodCField.value,
        disabled: this.dilutionPeriodCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodDField.key]: [
      {
        value: this.dilutionPeriodDField.value,
        disabled: this.dilutionPeriodDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionFactorAField.key]: [
      {
        value: this.dilutionFactorAField.value,
        disabled: this.dilutionFactorAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorBField.key]: [
      {
        value: this.dilutionFactorBField.value,
        disabled: this.dilutionFactorBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorCField.key]: [
      {
        value: this.dilutionFactorCField.value,
        disabled: this.dilutionFactorCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorDField.key]: [
      {
        value: this.dilutionFactorDField.value,
        disabled: this.dilutionFactorDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    // pH controls
    [this.phControlAField.key]: [
      {
        value: this.phControlAField.value,
        disabled: this.phControlAField.disabled,
      },
    ],
    [this.phControlBField.key]: [
      {
        value: this.phControlBField.value,
        disabled: this.phControlBField.disabled,
      },
    ],
    [this.phControlCField.key]: [
      {
        value: this.phControlCField.value,
        disabled: this.phControlCField.disabled,
      },
    ],
    [this.phControlDField.key]: [
      {
        value: this.phControlDField.value,
        disabled: this.phControlDField.disabled,
      },
    ],
    [this.targetPHAField.key]: [
      {
        value: this.targetPHAField.value,
        disabled: this.targetPHAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHBField.key]: [
      {
        value: this.targetPHBField.value,
        disabled: this.targetPHBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHCField.key]: [
      {
        value: this.targetPHCField.value,
        disabled: this.targetPHCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHDField.key]: [
      {
        value: this.targetPHDField.value,
        disabled: this.targetPHDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.inputTypeAField.key]: [
      {
        value: this.inputTypeAField.value,
        disabled: this.inputTypeAField.disabled,
      },
    ],
    [this.inputTypeBField.key]: [
      {
        value: this.inputTypeBField.value,
        disabled: this.inputTypeBField.disabled,
      },
    ],
    [this.inputTypeCField.key]: [
      {
        value: this.inputTypeCField.value,
        disabled: this.inputTypeCField.disabled,
      },
    ],
    [this.inputTypeDField.key]: [
      {
        value: this.inputTypeDField.value,
        disabled: this.inputTypeDField.disabled,
      },
    ],
    [this.pumpDurationAField.key]: [
      {
        value: this.pumpDurationAField.value,
        disabled: this.pumpDurationAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationBField.key]: [
      {
        value: this.pumpDurationBField.value,
        disabled: this.pumpDurationBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationCField.key]: [
      {
        value: this.pumpDurationCField.value,
        disabled: this.pumpDurationCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationDField.key]: [
      {
        value: this.pumpDurationDField.value,
        disabled: this.pumpDurationDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH 2xControl
    [this.ph2ControlAField.key]: [
      {
        value: this.ph2ControlAField.value,
        disabled: this.ph2ControlAField.disabled,
      },
    ],
    [this.ph2ControlBField.key]: [
      {
        value: this.ph2ControlBField.value,
        disabled: this.ph2ControlBField.disabled,
      },
    ],
    [this.targetPH2AField.key]: [
      {
        value: this.targetPH2AField.value,
        disabled: this.targetPH2AField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPH2BField.key]: [
      {
        value: this.targetPH2BField.value,
        disabled: this.targetPH2BField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDuration2AField.key]: [
      {
        value: this.pumpDuration2AField.value,
        disabled: this.pumpDuration2AField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDuration2BField.key]: [
      {
        value: this.pumpDuration2BField.value,
        disabled: this.pumpDuration2BField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH + Chemostat
    [this.phChemoControlAField.key]: [
      {
        value: this.phChemoControlAField.value,
        disabled: this.phChemoControlAField.disabled,
      },
    ],
    [this.phChemoControlBField.key]: [
      {
        value: this.phChemoControlBField.value,
        disabled: this.phChemoControlBField.disabled,
      },
    ],
    [this.targetPHChemoAField.key]: [
      {
        value: this.targetPHChemoAField.value,
        disabled: this.targetPHChemoAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHChemoBField.key]: [
      {
        value: this.targetPHChemoBField.value,
        disabled: this.targetPHChemoBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDurationChemoAField.key]: [
      {
        value: this.pumpDurationChemoAField.value,
        disabled: this.pumpDurationChemoAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationChemoBField.key]: [
      {
        value: this.pumpDurationChemoBField.value,
        disabled: this.pumpDurationChemoBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.inputTypeChemoAField.key]: [
      {
        value: this.inputTypeChemoAField.value,
        disabled: this.inputTypeChemoAField.disabled,
      },
    ],
    [this.inputTypeChemoBField.key]: [
      {
        value: this.inputTypeChemoBField.value,
        disabled: this.inputTypeChemoBField.disabled,
      },
    ],
    [this.flowRatePHAField.key]: [
      {
        value: this.flowRatePHAField.value,
        disabled: this.flowRatePHAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRatePHBField.key]: [
      {
        value: this.flowRatePHBField.value,
        disabled: this.flowRatePHBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Fluorescence
    [this.led1OnField.key]: [
      { value: this.led1OnField.value, disabled: this.led1OnField.disabled },
    ],
    [this.led2OnField.key]: [
      { value: this.led2OnField.value, disabled: this.led2OnField.disabled },
    ],
    [this.led3OnField.key]: [
      { value: this.led3OnField.value, disabled: this.led3OnField.disabled },
    ],
    [this.led1IntensityField.key]: [
      {
        value: this.led1IntensityField.value,
        disabled: this.led1IntensityField.disabled,
      },
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led2IntensityField.key]: [
      {
        value: this.led2IntensityField.value,
        disabled: this.led2IntensityField.disabled,
      },
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led3IntensityField.key]: [
      {
        value: this.led3IntensityField.value,
        disabled: this.led3IntensityField.disabled,
      },
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.integrationTimeAField.key]: [
      {
        value: this.integrationTimeAField.value,
        disabled: this.integrationTimeAField.disabled,
      },
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeBField.key]: [
      {
        value: this.integrationTimeBField.value,
        disabled: this.integrationTimeBField.disabled,
      },
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeCField.key]: [
      {
        value: this.integrationTimeCField.value,
        disabled: this.integrationTimeCField.disabled,
      },
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeDField.key]: [
      {
        value: this.integrationTimeDField.value,
        disabled: this.integrationTimeDField.disabled,
      },
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.specGainAField.key]: [
      {
        value: this.specGainAField.value,
        disabled: this.specGainAField.disabled,
      },
    ],
    [this.specGainBField.key]: [
      {
        value: this.specGainBField.value,
        disabled: this.specGainBField.disabled,
      },
    ],
    [this.specGainCField.key]: [
      {
        value: this.specGainCField.value,
        disabled: this.specGainCField.disabled,
      },
    ],
    [this.specGainDField.key]: [
      {
        value: this.specGainDField.value,
        disabled: this.specGainDField.disabled,
      },
    ],
    // Oxygen
    [this.salinityAField.key]: [
      { value: this.salinityAField.value, disabled: true },
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityBField.key]: [
      { value: this.salinityBField.value, disabled: true },
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityCField.key]: [
      { value: this.salinityCField.value, disabled: true },
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityDField.key]: [
      { value: this.salinityDField.value, disabled: true },
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    // Sparging
    [this.spargingControlTypeAField.key]: [
      {
        value: this.spargingControlTypeAField.value,
        disabled: this.spargingControlTypeAField.disabled,
      },
    ],
    [this.spargingControlTypeBField.key]: [
      {
        value: this.spargingControlTypeBField.value,
        disabled: this.spargingControlTypeBField.disabled,
      },
    ],
    [this.spargingControlTypeCField.key]: [
      {
        value: this.spargingControlTypeCField.value,
        disabled: this.spargingControlTypeCField.disabled,
      },
    ],
    [this.spargingControlTypeDField.key]: [
      {
        value: this.spargingControlTypeDField.value,
        disabled: this.spargingControlTypeDField.disabled,
      },
    ],
    [this.airflowAField.key]: [
      {
        value: this.airflowAField.value,
        disabled: this.airflowAField.disabled,
      },
      [RangeValidator(this.minAirflow, this.maxAirflow)],
    ],
    [this.airflowBField.key]: [
      {
        value: this.airflowBField.value,
        disabled: this.airflowBField.disabled,
      },
      [RangeValidator(this.minAirflow, this.maxAirflow)],
    ],
    [this.airflowCField.key]: [
      {
        value: this.airflowCField.value,
        disabled: this.airflowCField.disabled,
      },
      [RangeValidator(this.minAirflow, this.maxAirflow)],
    ],
    [this.airflowDField.key]: [
      {
        value: this.airflowDField.value,
        disabled: this.airflowDField.disabled,
      },
      [RangeValidator(this.minAirflow, this.maxAirflow)],
    ],
    [this.noteField.key]: [
      '',
      [
        Validators.minLength(this.minNoteLength),
        Validators.maxLength(this.maxNoteLength),
      ],
    ],
  });

  // ── Form accessor ──────────────────────────────────────────────────────────

  override get f(): FormGroup['controls'] {
    return this.experimentUpdateForm.controls;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params: Params) => {
        this.experimentId.set(parseInt(params.id as string, 10));
        this.navOptions = [
          {
            value: 'display',
            labelText: 'Display',
            link:
              '/experiment/' + this.experimentId().toString() + '/display',
          },
          {
            value: 'control',
            labelText: 'Control',
            link:
              '/experiment/' + this.experimentId().toString() + '/control',
          },
          {
            value: 'device-errors',
            labelText: 'Device Errors',
            link:
              '/experiment/' +
              this.experimentId().toString() +
              '/device-errors',
          },
        ];
      });

    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser.set(user);
      });

    if (this.experimentService.changelogPaginationCache !== null) {
      this.pageSize.set(
        this.experimentService.changelogPaginationCache.pageSize,
      );
      this.pageRequested.set(
        this.experimentService.changelogPaginationCache.pageNumber,
      );
      this.paginationControls.patchValue({
        [this.changeLogsPerPageField.key]: this.pageSize().toString(),
      });
    }

    if (this.experimentService.sort !== null) {
      this.currentSort.set(this.experimentService.sort);
    }

    this.laboratory.set(this.authenticationService.selectedLaboratory);
    if (this.laboratory()) {
      this.getLabMembers();
    }

    // Now get experiment
    this.getExperiment();

    if (this.hasEditAccess()) {
      this.initialiseSubscriptions();
    }

    // Check for cached form on init (after login redirect)
    this.unsavedCachedData.set(
      this.experimentService.retrieveAndClearUnsavedCache as ExperimentUpdate,
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    // Cache form if we're being destroyed due to 401 navigation
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.experimentUpdateForm.pristine
    ) {
      const formValues = this.getCurrentFormValues();
      this.experimentService.cacheUnsavedParameters(formValues);
    }
  }

  // ── Navigation guard ───────────────────────────────────────────────────────

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  protected override isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        this.experimentUpdateForm.pristine ||
        !this.currentUser() ||
        this.authEventService.isUnauthorizedNavigationPending
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation
          .pipe(take(1))
          .subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }

  // ── Device / experiment operations ────────────────────────────────────────

  areDevicesPaused(): boolean {
    const paused = this.experiment.devices.find(
      (device) => device.deviceStatusId === DeviceSetupStatus.Paused,
    );
    if (paused) {
      return true;
    } else {
      return false;
    }
  }

  initialiseSubscriptions(): void {
    // Stirring speed controls
    (['A', 'B', 'C', 'D'] as Flask[]).forEach((flask) => {
      const setSpeedField = this[`setStirringSpeed${flask}Field` as const];
      this.experimentUpdateForm
        .get(setSpeedField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.updateStirringSpeedState(flask, value);
        });
    });

    // Temperature controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const tempControlField = this[`temperatureControl${flask}Field` as const];
      this.experimentUpdateForm
        .get(tempControlField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'temperature', value);
        });
    });

    // Turbidostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const turbActiveField = this[`turb${flask}ActiveField` as const];
      this.experimentUpdateForm
        .get(turbActiveField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'turbidostat', value);
        });
    });

    // Chemostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const chemoActiveField = this[`chemo${flask}ActiveField` as const];
      this.experimentUpdateForm
        .get(chemoActiveField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'chemostat', value);
        });
    });

    // pH controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const phControlField = this[`phControl${flask}Field` as const];
      this.experimentUpdateForm
        .get(phControlField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'ph', value);
        });
    });

    // pH 2x controls
    (['A', 'B'] as const).forEach((flask) => {
      const ph2ControlField = this[`ph2Control${flask}Field` as const];
      this.experimentUpdateForm
        .get(ph2ControlField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'ph2', value);
        });
    });

    // pH Chemo controls
    (['A', 'B'] as const).forEach((flask) => {
      const phChemoControlField = this[`phChemoControl${flask}Field` as const];
      this.experimentUpdateForm
        .get(phChemoControlField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'phChemo', value);
        });
    });

    // Morbidostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const morbidostatActiveField = this[`morbidostat${flask}ActiveField` as const];
      this.experimentUpdateForm
        .get(morbidostatActiveField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'morbidostat', value);
        });
    });

    // Sparging controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const spargingControlField =
        this[`spargingControlType${flask}Field` as const];
      this.experimentUpdateForm
        .get(spargingControlField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          const isDisabled = value.includes('Disabled');
          this.setFlaskControlState(flask, 'sparging', !isDisabled);
        });
    });

    this.paginationControls
      .get(this.changeLogsPerPageField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((numOfChangelogs) => {
        this.pageSize.set(numOfChangelogs as number);
        this.pageRequested.set(1);
        this.getExperimentChangelog();
      });

    // Notification controls
    this.experimentUpdateForm
      .get(this.enableNotificationsField.key)
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((enabled) => {
        const emailControl = this.experimentUpdateForm.get(this.notificationEmailField.key);
        if (!emailControl) return;
        emailControl.setValidators(enabled ? [Validators.required] : []);
        if (enabled) {
          emailControl.enable();
          const notificationEmail = this.experiment?.notificationEmail;
          const currentUserEmail = this.currentUser()?.email || '';
          emailControl.setValue(notificationEmail || currentUserEmail);
        } else {
          emailControl.setValue('');
          emailControl.disable();
        }
        emailControl.updateValueAndValidity();
      });

    this.subscribeNotificationEmail();
  }

  onStopExperiment(): void {
    if (!this.hasEditAccess() || this.stopExperimentProcessing()) return;

    this.stopExperimentProcessing.set(true);
    this.error.set(null);

    this.stopExperiment = {
      id: this.experiment.id,
      experimentStatusId: ExperimentStatus.Complete,
    };
    this.experimentService
      .updateActiveExperiment(this.stopExperiment)
      .subscribe({
        next: (response: Experiment) => {
          this.error.set(null);
          this.stopExperimentProcessing.set(false);
          this.experiment = response;
          this.cdr.markForCheck();
          // Now update the forms with the updated experiment
          this.updateFormValues(response);
          // And get the update to the changelog
          this.getExperimentChangelog();
          // Close modal
          this.closeModal('stop-experiment');
        },
        error: (error: ErrorObject) => {
          this.error.set(error);
          this.stopExperimentProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  handleUndoButton(): void {
    this.updateFormValues(this.experiment);
  }

  pauseRestartButtonClicked(): void {
    if (!this.hasEditAccess()) return;
    const devicePaused = this.areDevicesPaused();
    if (devicePaused) {
      this.pauseDeviceTitleText.set('Restart device');
      this.pauseDeviceValidationText.set('restart');
      this.pauseDeviceButtonText.set('restart');
    } else {
      this.pauseDeviceTitleText.set('Pause device');
      this.pauseDeviceValidationText.set('pause');
      this.pauseDeviceButtonText.set('pause');
    }
    this.openModal('pause-devices');
  }

  pauseDevicesOnExperiment(): void {
    if (!this.hasEditAccess() || this.pauseDevicesProcessing()) return;

    this.pauseDevicesProcessing.set(true);
    this.error.set(null);

    const pauseOrResume = this.areDevicesPaused() === true ? 'true' : 'false';
    this.experimentService
      .pauseDevicesOnExperiment(this.experiment.id.toString(), pauseOrResume)
      .subscribe({
        next: () => {
          this.error.set(null);
          this.pauseDevicesProcessing.set(false);
          this.getExperiment();
          this.closeModal('pause-devices');
        },
        error: (error: ErrorObject) => {
          this.error.set(error);
          this.pauseDevicesProcessing.set(false);
          this.closeModal('pause-devices');
          this.openModal('error');
        },
      });
  }

  getExperiment(dontCallChangelog = false): void {
    this.loading.set(true);
    this.getExperimentError.set(null);

    this.experimentService.getExperimentById(this.experimentId()).subscribe({
      next: (response: Experiment) => {
        if (
          response.experimentStatusId !== ExperimentStatus.Draft &&
          response.experimentStatusId !== ExperimentStatus.DraftLocked
        ) {
          this.experimentDataLastUpdated.set(new Date());
          this.experiment = response;
          this.devices = response.devices;
          this.loading.set(false);
          this.cdr.markForCheck();
          // Now we can set min culture temp to the ambient temp
          this.minCultureTemp = this.experiment.ambientTemperature;
          // Initialise the forms with the data
          this.updateFormValues(response);
          // Fetch the experiment data (calling it in tandem with getExperiment causes null
          // exception on backend)
          if (!dontCallChangelog) {
            this.getExperimentChangelog();
          }
        } else {
          this.loading.set(false);
          this.getExperimentError.set({
            status: -1,
            message: 'Experiment is not active',
            code: null,
          });
        }
      },
      error: (error: ErrorObject) => {
        this.getExperimentError.set(error);
        this.loading.set(false);
      },
    });
  }

  updateSpargingValidators(min: number, max: number): void {
    this.minAirflow = min;
    this.maxAirflow = max;

    const controls = [
      this.airflowAField.key,
      this.airflowBField.key,
      this.airflowCField.key,
      this.airflowDField.key,
    ];

    for (const key of controls) {
      const control = this.experimentUpdateForm.controls[key];
      control.clearValidators();
      control.setValidators([RangeValidator(this.minAirflow, this.maxAirflow)]);
      control.updateValueAndValidity();
      control.markAsTouched();
    }
  }

  getExperimentChangelog(): void {
    this.changelogError.set(null);
    this.experimentService
      .getExperimentChangelog(
        this.experimentId(),
        this.currentSort().sortBy,
        this.currentSort().sortDirection,
        this.pageSize(),
        this.pageRequested(),
      )
      .subscribe({
        next: (response: ChangelogResponse) => {
          this.changelogError.set(null);
          this.changelogList.set(response.changelogList);
          // Set pagination vars from response headers
          this.currentPage.set(response.paginationData.CurrentPage);
          this.pageSize.set(response.paginationData.PageSize);
          this.totalPages.set(response.paginationData.TotalPages);
          this.hasNext.set(response.paginationData.HasNext);
          this.hasPrevious.set(response.paginationData.HasPrevious);
          this.experimentService.onUpdateChangelogPageNumber(
            this.currentPage(),
          );
          this.experimentService.onUpdateChangelogPageSize(this.pageSize());
          this.getExperiment(true);
        },
        error: (error: ErrorObject) => {
          this.changelogError.set(error);
        },
      });
  }

  updateFormValues(experimentData: Experiment | ExperimentUpdate): void {
    // Combine all form values using helper methods
    const formValues = {
      ...this.getBasicFormValues(experimentData),
      ...this.getDefaultModuleFormValues(experimentData),
      ...this.getTurbidostatFormValues(experimentData),
      ...this.getChemostatFormValues(experimentData),
      ...this.getMorbidostatFormValues(experimentData),
      ...this.getPHControlFormValues(experimentData),
      ...this.getFluorescenceFormValues(experimentData),
      ...this.getOxygenFormValues(experimentData),
      ...this.getSpargingFormValues(experimentData),
    };

    this.experimentUpdateForm.patchValue(
      formValues as { [key: string]: string | number | boolean },
    );

    // Update form values with any unsaved cached data
    if (this.unsavedCachedData()) {
      const cachedFormValues = {
        ...this.getBasicFormValues(this.unsavedCachedData()),
        ...this.getDefaultModuleFormValues(this.unsavedCachedData()),
        ...this.getTurbidostatFormValues(this.unsavedCachedData()),
        ...this.getChemostatFormValues(this.unsavedCachedData()),
        ...this.getMorbidostatFormValues(this.unsavedCachedData()),
        ...this.getPHControlFormValues(this.unsavedCachedData()),
        ...this.getFluorescenceFormValues(this.unsavedCachedData()),
        ...this.getOxygenFormValues(this.unsavedCachedData()),
        ...this.getSpargingFormValues(this.unsavedCachedData()),
      };

      this.experimentUpdateForm.patchValue(
        cachedFormValues as { [key: string]: string | number | boolean },
      );
    }

    // Both patchValue calls above fire enableNotifications.valueChanges, which
    // overwrites notificationEmail with the current user's email when the value
    // from the API is null. Re-apply the authoritative email from the final data
    // source without emitting before enableFormControls() runs.
    const finalData = this.unsavedCachedData() ?? experimentData;
    const restoredEmail = finalData.notificationEmail ?? '';
    this.experimentUpdateForm
      .get(this.notificationEmailField.key)
      ?.setValue(restoredEmail, { emitEvent: false });

    this.enableFormControls();

    this.notificationEmailValue.set(restoredEmail);

    this.experimentUpdateForm.markAsPristine();
  }

  enableFormControls(): void {
    if (this.hasEditAccess() && this.experiment?.isDeleted === false) {
      this.experimentUpdateForm.controls[this.enableNotificationsField.key].enable({ emitEvent: false });
      if (this.f[this.enableNotificationsField.key].value) {
        this.experimentUpdateForm.controls[this.notificationEmailField.key].enable();
      }
      this.experimentUpdateForm.controls[this.organismField.key].enable();
      this.experimentUpdateForm.controls[this.strainField.key].enable();
      this.experimentUpdateForm.controls[this.mediaField.key].enable();
      this.experimentUpdateForm.controls[this.experimentTimeField.key].enable();
      this.experimentUpdateForm.controls[this.samplingTimeField.key].enable();
      this.experimentUpdateForm.controls[
        this.setStirringSpeedAField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.setStirringSpeedBField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.setStirringSpeedCField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.setStirringSpeedDField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.temperatureControlAField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.temperatureControlBField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.temperatureControlCField.key
      ].enable();
      this.experimentUpdateForm.controls[
        this.temperatureControlDField.key
      ].enable();
      if (this.experiment.speedRPMA !== 0) {
        this.experimentUpdateForm.controls[this.speedAField.key].enable();
      }
      if (this.experiment.speedRPMA !== 0) {
        this.experimentUpdateForm.controls[this.speedBField.key].enable();
      }
      if (this.experiment.speedRPMA !== 0) {
        this.experimentUpdateForm.controls[this.speedCField.key].enable();
      }
      if (this.experiment.speedRPMA !== 0) {
        this.experimentUpdateForm.controls[this.speedDField.key].enable();
      }

      if (this.f.temperatureControlA.value === true) {
        this.experimentUpdateForm.controls[this.temperatureAField.key].enable();
      }
      if (this.f.temperatureControlB.value === true) {
        this.experimentUpdateForm.controls[this.temperatureBField.key].enable();
      }
      if (this.f.temperatureControlC.value === true) {
        this.experimentUpdateForm.controls[this.temperatureCField.key].enable();
      }
      if (this.f.temperatureControlD.value === true) {
        this.experimentUpdateForm.controls[this.temperatureDField.key].enable();
      }
      // LED controls should be enabled for all experiment types
      this.f.led1On.enable();
      this.f.led2On.enable();
      this.f.led3On.enable();
      this.f.led1Intensity.enable();
      this.f.led2Intensity.enable();
      this.f.led3Intensity.enable();

      // Other fluorescence fields only enabled when useFluorescence is true
      if (this.experiment.useFluorescence) {
        this.f.integrationTimeA.enable();
        this.f.integrationTimeB.enable();
        this.f.integrationTimeC.enable();
        this.f.integrationTimeD.enable();
        this.f.specGainA.enable();
        this.f.specGainB.enable();
        this.f.specGainC.enable();
        this.f.specGainD.enable();
      } else {
        this.f.integrationTimeA.disable();
        this.f.integrationTimeB.disable();
        this.f.integrationTimeC.disable();
        this.f.integrationTimeD.disable();
        this.f.specGainA.disable();
        this.f.specGainB.disable();
        this.f.specGainC.disable();
        this.f.specGainD.disable();
      }

      // Salinity fields are never updateable in active experiments
      this.f.salinityA.disable();
      this.f.salinityB.disable();
      this.f.salinityC.disable();
      this.f.salinityD.disable();

      switch (this.experiment.experimentMode) {
        case ExperimentMode.Turbidostat:
          this.f.turbAActive.enable();
          this.f.turbBActive.enable();
          this.f.turbCActive.enable();
          this.f.turbDActive.enable();
          // Target OD fields only editable if turbidostat control is set to true
          if (this.f.turbAActive.value) {
            this.f.targetODA.enable();
          }
          if (this.f.turbBActive.value) {
            this.f.targetODB.enable();
          }
          if (this.f.turbCActive.value) {
            this.f.targetODC.enable();
          }
          if (this.f.turbDActive.value) {
            this.f.targetODD.enable();
          }
          break;
        case ExperimentMode.Chemostat:
          this.f.chemoAActive.enable();
          this.f.chemoBActive.enable();
          this.f.chemoCActive.enable();
          this.f.chemoDActive.enable();
          if (this.f.chemoAActive.value) {
            this.f.flowRateA.enable();
          }
          if (this.f.chemoBActive.value) {
            this.f.flowRateB.enable();
          }
          if (this.f.chemoCActive.value) {
            this.f.flowRateC.enable();
          }
          if (this.f.chemoDActive.value) {
            this.f.flowRateD.enable();
          }
          break;
        case ExperimentMode.PHControl:
          this.f.phControlA.enable();
          this.f.phControlB.enable();
          this.f.phControlC.enable();
          this.f.phControlD.enable();
          // pH fields only editable if phControl is set to true
          if (this.f.phControlA.value === true) {
            this.f.targetPHA.enable();
            this.f.pumpDurationA.enable();
            this.f.inputTypeA.enable();
          }
          if (this.f.phControlB.value === true) {
            this.f.targetPHB.enable();
            this.f.pumpDurationB.enable();
            this.f.inputTypeB.enable();
          }
          if (this.f.phControlC.value === true) {
            this.f.targetPHC.enable();
            this.f.pumpDurationC.enable();
            this.f.inputTypeC.enable();
          }
          if (this.f.phControlD.value === true) {
            this.f.targetPHD.enable();
            this.f.pumpDurationD.enable();
            this.f.inputTypeD.enable();
          }
          break;
        case ExperimentMode.PHControlDouble:
          this.f.ph2ControlA.enable();
          this.f.ph2ControlB.enable();
          if (this.f.ph2ControlA.value === true) {
            this.f.target2PHA.enable();
            this.f.pumpDuration2A.enable();
          }
          if (this.f.ph2ControlB.value === true) {
            this.f.target2PHB.enable();
            this.f[this.pumpDuration2BField.key].enable();
          }
          break;
        case ExperimentMode.PHControlChemostat:
          this.f[this.phChemoControlAField.key].enable();
          this.f[this.phChemoControlBField.key].enable();
          if (this.f[this.phChemoControlAField.key].value === true) {
            this.f[this.targetPHChemoAField.key].enable();
            this.f[this.pumpDurationChemoAField.key].enable();
            this.f[this.inputTypeChemoAField.key].enable();
            this.f[this.flowRatePHAField.key].enable();
          }
          if (this.f[this.phChemoControlBField.key].value === true) {
            this.f[this.targetPHChemoBField.key].enable();
            this.f[this.pumpDurationChemoBField.key].enable();
            this.f[this.inputTypeChemoBField.key].enable();
            this.f[this.flowRatePHBField.key].enable();
          }
          break;
        case ExperimentMode.Morbidostat:
          this.f[this.morbidostatAActiveField.key].enable();
          this.f[this.morbidostatBActiveField.key].enable();
          this.f[this.morbidostatCActiveField.key].enable();
          this.f[this.morbidostatDActiveField.key].enable();
          if (this.f[this.morbidostatAActiveField.key].value === true) {
            this.f[this.thresholdODAField.key].enable();
            this.f[this.dilutionPeriodAField.key].enable();
            this.f[this.dilutionFactorAField.key].enable();
          }
          if (this.f[this.morbidostatBActiveField.key].value === true) {
            this.f[this.thresholdODBField.key].enable();
            this.f[this.dilutionPeriodBField.key].enable();
            this.f[this.dilutionFactorBField.key].enable();
          }
          if (this.f[this.morbidostatCActiveField.key].value === true) {
            this.f[this.thresholdODCField.key].enable();
            this.f[this.dilutionPeriodCField.key].enable();
            this.f[this.dilutionFactorCField.key].enable();
          }
          if (this.f[this.morbidostatDActiveField.key].value === true) {
            this.f[this.thresholdODDField.key].enable();
            this.f[this.dilutionPeriodDField.key].enable();
            this.f[this.dilutionFactorDField.key].enable();
          }
          break;
        default:
          break;
      }

      // If sparging controls are not disabled, enable the airflow fields
      if (this.experiment.useSparging) {
        this.f.spargingControlTypeA.enable();
        this.f.spargingControlTypeB.enable();
        this.f.spargingControlTypeC.enable();
        this.f.spargingControlTypeD.enable();
        if (this.experiment.spargingControlTypeA !== SpargingStatus.Disabled) {
          this.f.airflowA.enable();
        }
        if (this.experiment.spargingControlTypeB !== SpargingStatus.Disabled) {
          this.f.airflowB.enable();
        }
        if (this.experiment.spargingControlTypeC !== SpargingStatus.Disabled) {
          this.f.airflowC.enable();
        }
        if (this.experiment.spargingControlTypeD !== SpargingStatus.Disabled) {
          this.f.airflowD.enable();
        }

        this.updateSpargingValidators(
          this.experiment.minAirFlow,
          this.experiment.maxAirFlow,
        );
      }

      // If the experiment is not active, then only allow notes to be updated
      if (this.experiment.experimentStatusId !== ExperimentStatus.Active) {
        this.experimentUpdateForm.disable();
        this.f.noteField.enable();
      }
    }

    if (!this.hasEditAccess() || this.experiment.isDeleted === true) {
      this.experimentUpdateForm.disable();
    }

    this.experimentUpdateForm.markAsPristine();
  }

  onSubmitExperimentUpdateForm(): void {
    // If form is already being submitted, exit
    if (this.updateExperimentProcessing()) {
      return;
    }
    this.updateExperimentProcessing.set(true);
    this.error.set(null);

    const formValues = this.getCurrentFormValues();

    this.experimentService.updateActiveExperiment(formValues).subscribe({
      next: (response: Experiment) => {
        this.error.set(null);
        this.updateExperimentProcessing.set(false);
        this.modalService.open('update-experiment');
        this.updateFormValues(response);
        this.getExperimentChangelog();
      },
      error: (error: ErrorObject) => {
        this.error.set(error);
        this.updateExperimentProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  getCurrentFormValues(): ExperimentUpdate {
    const turbidostatValues = this.getTurbidostatValues();
    const defaultModuleValues = this.getDefaultModuleValues();
    const chemostatValues = this.getChemostatValues();
    const morbidostatValues = this.getMorbidostatValues();
    const phControlValues = this.getPHControlValues();
    const fluorescenceValues = this.getFluorescenceValues();
    const spargingValues = this.getSpargingValues();

    return {
      id: this.experimentId(),
      organism: this.f[this.organismField.key].value as string,
      strain: this.f[this.strainField.key].value as string,
      media: this.f[this.mediaField.key].value as string,
      // Default module values
      temperatureControlA: defaultModuleValues.temperatureControlA,
      temperatureControlB: defaultModuleValues.temperatureControlB,
      temperatureControlC: defaultModuleValues.temperatureControlC,
      temperatureControlD: defaultModuleValues.temperatureControlD,
      cultureTemperatureA: defaultModuleValues.temperatureA,
      cultureTemperatureB: defaultModuleValues.temperatureB,
      cultureTemperatureC: defaultModuleValues.temperatureC,
      cultureTemperatureD: defaultModuleValues.temperatureD,
      samplingInterval: defaultModuleValues.samplingTime,
      duration: defaultModuleValues.experimentTime,
      speedRPMA: defaultModuleValues.speedA,
      speedRPMB: defaultModuleValues.speedB,
      speedRPMC: defaultModuleValues.speedC,
      speedRPMD: defaultModuleValues.speedD,
      // Turbidostat controls
      ...turbidostatValues,
      // Chemostat controls
      ...chemostatValues,
      // Morbidostat controls
      ...morbidostatValues,
      // pH controls
      ...phControlValues,
      // LED controls, submitted even if fluorescence is not being measured
      led1On: fluorescenceValues.led1On,
      led2On: fluorescenceValues.led2On,
      led3On: fluorescenceValues.led3On,
      led1Intensity: fluorescenceValues.led1Intensity,
      led2Intensity: fluorescenceValues.led2Intensity,
      led3Intensity: fluorescenceValues.led3Intensity,
      integrationTimeA: fluorescenceValues.integrationTimeA,
      integrationTimeB: fluorescenceValues.integrationTimeB,
      integrationTimeC: fluorescenceValues.integrationTimeC,
      integrationTimeD: fluorescenceValues.integrationTimeD,
      specGainA: fluorescenceValues.specGainA,
      specGainB: fluorescenceValues.specGainB,
      specGainC: fluorescenceValues.specGainC,
      specGainD: fluorescenceValues.specGainD,
      // Sparging controls
      spargingControlTypeA: spargingValues.spargingControlTypeA,
      spargingControlTypeB: spargingValues.spargingControlTypeB,
      spargingControlTypeC: spargingValues.spargingControlTypeC,
      spargingControlTypeD: spargingValues.spargingControlTypeD,
      airFlowA: spargingValues.airFlowA,
      airFlowB: spargingValues.airFlowB,
      airFlowC: spargingValues.airFlowC,
      airFlowD: spargingValues.airFlowD,
      notes: this.f[this.noteField.key].value as string,
      enableNotifications: this.f[this.enableNotificationsField.key].value as boolean,
      notificationEmail: this.f[this.enableNotificationsField.key].value
        ? (this.f[this.notificationEmailField.key].value as string)
        : null,
    };
  }

  deleteExperiment(): void {
    // If request has already been submitted, exit
    if (this.deleteIsProcessing()) {
      return;
    }

    this.deleteError.set(null);
    this.deleteIsProcessing.set(true);

    this.experimentService
      .deleteExperimentById(this.experimentId())
      .subscribe({
        next: () => {
          this.deleteIsProcessing.set(false);
          this.closeModal('delete-experiment');
          this.openModal('delete-success');
        },
        error: (error: ErrorObject) => {
          this.deleteIsProcessing.set(false);
          this.deleteError.set(error);
          this.closeModal('delete-experiment');
          this.openModal('delete-error');
        },
      });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
      case 'close-button':
        this.closeModal('error');
        break;
      case 'close-success-button':
        this.closeModal('update-experiment');
        break;
      case 'delete-success-button':
        this.closeModal('delete-success');
        this.allowNavigation.next(true);
        void this.router.navigate(['dashboard']);
        break;
      case 'error-primary-button':
        this.closeModal('delete-error');
        this.allowNavigation.next(true);
        void this.router.navigate(['dashboard']);
        break;
      case 'error-secondary-button':
        this.closeModal('delete-error');
        break;
      default:
        break;
    }
  }

  // ── Copy methods (override base to write to experimentUpdateForm) ───────────

  override copyTempControlValues(): void {
    const controlValueA = this.f[this.temperatureControlAField.key]
      .value as boolean;
    const tempValueA = this.f[this.temperatureAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.temperatureControlBField, this.temperatureBField],
      [this.temperatureControlCField, this.temperatureCField],
      [this.temperatureControlDField, this.temperatureDField],
    ];
    targets.forEach(([controlField, tempField]) => {
      this.experimentUpdateForm.controls[controlField.key].setValue(
        controlValueA,
      );
      this.experimentUpdateForm.controls[controlField.key].markAsDirty();
      this.experimentUpdateForm.controls[tempField.key].setValue(tempValueA);
      this.experimentUpdateForm.controls[tempField.key].markAsDirty();
    });
  }

  override copyStirringSpeedValues(): void {
    const setSpeedValueA = this.f[this.setStirringSpeedAField.key]
      .value as boolean;
    const speedValueA = this.f[this.speedAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.setStirringSpeedBField, this.speedBField],
      [this.setStirringSpeedCField, this.speedCField],
      [this.setStirringSpeedDField, this.speedDField],
    ];
    targets.forEach(([setSpeedField, speedField]) => {
      this.experimentUpdateForm.controls[setSpeedField.key].setValue(
        setSpeedValueA,
      );
      this.experimentUpdateForm.controls[setSpeedField.key].markAsDirty();
      this.experimentUpdateForm.controls[speedField.key].setValue(speedValueA);
      this.experimentUpdateForm.controls[speedField.key].markAsDirty();
    });
  }

  override copyTurbidostatControlValues(): void {
    const turbActiveValueA = this.f[this.turbAActiveField.key].value as boolean;
    const targetODValueA = this.f[this.targetODAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.turbBActiveField, this.targetODBField],
      [this.turbCActiveField, this.targetODCField],
      [this.turbDActiveField, this.targetODDField],
    ];
    targets.forEach(([activeField, targetODField]) => {
      this.experimentUpdateForm.controls[activeField.key].setValue(
        turbActiveValueA,
      );
      this.experimentUpdateForm.controls[activeField.key].markAsDirty();
      this.experimentUpdateForm.controls[targetODField.key].setValue(
        targetODValueA,
      );
      this.experimentUpdateForm.controls[targetODField.key].markAsDirty();
    });
  }

  override copyChemostatControlValues(): void {
    const chemoActiveValueA = this.f[this.chemoAActiveField.key]
      .value as boolean;
    const flowRateValueA = this.f[this.flowRateAField.key].value as number;
    const targets: [FormBase<boolean>, FormBase<number>][] = [
      [this.chemoBActiveField, this.flowRateBField],
      [this.chemoCActiveField, this.flowRateCField],
      [this.chemoDActiveField, this.flowRateDField],
    ];
    targets.forEach(([activeField, flowRateField]) => {
      this.experimentUpdateForm.controls[activeField.key].setValue(
        chemoActiveValueA,
      );
      this.experimentUpdateForm.controls[activeField.key].markAsDirty();
      this.experimentUpdateForm.controls[flowRateField.key].setValue(
        flowRateValueA,
      );
      this.experimentUpdateForm.controls[flowRateField.key].markAsDirty();
    });
  }

  override copyPHValues(): void {
    const phControlValueA = this.f[this.phControlAField.key].value as boolean;
    const targetPHValueA = this.f[this.targetPHAField.key].value as number;
    const pumpDurationValueA = this.f[this.pumpDurationAField.key]
      .value as number;
    const inputTypeValueA = this.f[this.inputTypeAField.key].value as
      | string
      | null;
    const inputTypeBaseValueA = inputTypeValueA?.slice(0, -1) ?? null;
    const targets: [
      FormBase<boolean>,
      FormBase<number>,
      FormBase<number>,
      FormBase<string>,
      string,
    ][] = [
      [
        this.phControlBField,
        this.targetPHBField,
        this.pumpDurationBField,
        this.inputTypeBField,
        'B',
      ],
      [
        this.phControlCField,
        this.targetPHCField,
        this.pumpDurationCField,
        this.inputTypeCField,
        'C',
      ],
      [
        this.phControlDField,
        this.targetPHDField,
        this.pumpDurationDField,
        this.inputTypeDField,
        'D',
      ],
    ];
    targets.forEach(
      ([
        phControlField,
        targetPHField,
        pumpDurationField,
        inputTypeField,
        suffix,
      ]) => {
        this.experimentUpdateForm.controls[phControlField.key].setValue(
          phControlValueA,
        );
        this.experimentUpdateForm.controls[phControlField.key].markAsDirty();
        this.experimentUpdateForm.controls[targetPHField.key].setValue(
          targetPHValueA,
        );
        this.experimentUpdateForm.controls[targetPHField.key].markAsDirty();
        this.experimentUpdateForm.controls[pumpDurationField.key].setValue(
          pumpDurationValueA,
        );
        this.experimentUpdateForm.controls[pumpDurationField.key].markAsDirty();
        if (inputTypeBaseValueA !== null) {
          this.experimentUpdateForm.controls[inputTypeField.key].setValue(
            `${inputTypeBaseValueA}${suffix}`,
          );
          this.experimentUpdateForm.controls[inputTypeField.key].markAsDirty();
        }
      },
    );
  }

  override copyPHDoubleValues(): void {
    this.experimentUpdateForm.controls[this.ph2ControlBField.key].setValue(
      this.f[this.ph2ControlAField.key].value as boolean,
    );
    this.experimentUpdateForm.controls[this.ph2ControlBField.key].markAsDirty();
    this.experimentUpdateForm.controls[this.targetPH2BField.key].setValue(
      this.f[this.targetPH2AField.key].value as number,
    );
    this.experimentUpdateForm.controls[this.targetPH2BField.key].markAsDirty();
    this.experimentUpdateForm.controls[this.pumpDuration2BField.key].setValue(
      this.f[this.pumpDuration2AField.key].value as number,
    );
    this.experimentUpdateForm.controls[this.pumpDuration2BField.key].markAsDirty();
  }

  override copyMorbidostatValues(): void {
    const activeValue = this.f[this.morbidostatAActiveField.key].value as boolean;
    const thresholdValue = this.f[this.thresholdODAField.key].value as number;
    const dilutionPeriodValue = this.f[this.dilutionPeriodAField.key].value as number;
    const dilutionFactorValue = this.f[this.dilutionFactorAField.key].value as number;
    const targets: [
      FormBase<boolean>,
      FormBase<number>,
      FormBase<number>,
      FormBase<number>,
    ][] = [
      [
        this.morbidostatBActiveField,
        this.thresholdODBField,
        this.dilutionPeriodBField,
        this.dilutionFactorBField,
      ],
      [
        this.morbidostatCActiveField,
        this.thresholdODCField,
        this.dilutionPeriodCField,
        this.dilutionFactorCField,
      ],
      [
        this.morbidostatDActiveField,
        this.thresholdODDField,
        this.dilutionPeriodDField,
        this.dilutionFactorDField,
      ],
    ];
    for (const [active, threshold, dilutionPeriod, dilutionFactor] of targets) {
      this.experimentUpdateForm.controls[active.key].setValue(activeValue);
      this.experimentUpdateForm.controls[active.key].markAsDirty();
      this.experimentUpdateForm.controls[threshold.key].setValue(thresholdValue);
      this.experimentUpdateForm.controls[threshold.key].markAsDirty();
      this.experimentUpdateForm.controls[dilutionPeriod.key].setValue(dilutionPeriodValue);
      this.experimentUpdateForm.controls[dilutionPeriod.key].markAsDirty();
      this.experimentUpdateForm.controls[dilutionFactor.key].setValue(dilutionFactorValue);
      this.experimentUpdateForm.controls[dilutionFactor.key].markAsDirty();
    }
  }

  override copyPHChemostatValues(): void {
    const inputTypeChemoValueA = this.f[this.inputTypeChemoAField.key].value as
      | string
      | null;
    const inputTypeChemoBaseValueA = inputTypeChemoValueA?.slice(0, -1) ?? null;
    this.experimentUpdateForm.controls[
      this.phChemoControlBField.key
    ].setValue(this.f[this.phChemoControlAField.key].value as boolean);
    this.experimentUpdateForm.controls[this.phChemoControlBField.key].markAsDirty();
    this.experimentUpdateForm.controls[this.targetPHChemoBField.key].setValue(
      this.f[this.targetPHChemoAField.key].value as number,
    );
    this.experimentUpdateForm.controls[this.targetPHChemoBField.key].markAsDirty();
    this.experimentUpdateForm.controls[
      this.pumpDurationChemoBField.key
    ].setValue(this.f[this.pumpDurationChemoAField.key].value as number);
    this.experimentUpdateForm.controls[this.pumpDurationChemoBField.key].markAsDirty();
    this.experimentUpdateForm.controls[this.flowRatePHBField.key].setValue(
      this.f[this.flowRatePHAField.key].value as number,
    );
    this.experimentUpdateForm.controls[this.flowRatePHBField.key].markAsDirty();
    if (inputTypeChemoBaseValueA !== null) {
      this.experimentUpdateForm.controls[
        this.inputTypeChemoBField.key
      ].setValue(`${inputTypeChemoBaseValueA}B`);
      this.experimentUpdateForm.controls[this.inputTypeChemoBField.key].markAsDirty();
    }
  }

  override copyOxygenValues(): void {
    const salinityValueA = this.f[this.salinityAField.key].value as number;
    [this.salinityBField, this.salinityCField, this.salinityDField].forEach(
      (field) => {
        this.experimentUpdateForm.controls[field.key].setValue(salinityValueA);
        this.experimentUpdateForm.controls[field.key].markAsDirty();
      },
    );
  }

  override copyLEDValues(): void {
    const led1OnValue = this.f[this.led1OnField.key].value as boolean;
    const led1IntensityValue = this.f[this.led1IntensityField.key]
      .value as number;
    const integrationTimeValueA = this.f[this.integrationTimeAField.key]
      .value as number;
    const specGainValueA = this.f[this.specGainAField.key].value as
      | string
      | null;
    const specGainBaseValueA = specGainValueA?.slice(0, -1) ?? null;

    (
      [
        [this.led2OnField, this.led2IntensityField],
        [this.led3OnField, this.led3IntensityField],
      ] as [FormBase<boolean>, FormBase<number>][]
    ).forEach(([onField, intensityField]) => {
      this.experimentUpdateForm.controls[onField.key].setValue(led1OnValue);
      this.experimentUpdateForm.controls[onField.key].markAsDirty();
      this.experimentUpdateForm.controls[intensityField.key].setValue(
        led1IntensityValue,
      );
      this.experimentUpdateForm.controls[intensityField.key].markAsDirty();
    });

    (
      [
        [this.integrationTimeBField, this.specGainBField, 'B'],
        [this.integrationTimeCField, this.specGainCField, 'C'],
      ] as [FormBase<number>, FormBase<string>, string][]
    ).forEach(([integrationField, specGainField, suffix]) => {
      this.experimentUpdateForm.controls[integrationField.key].setValue(
        integrationTimeValueA,
      );
      this.experimentUpdateForm.controls[integrationField.key].markAsDirty();
      if (specGainBaseValueA !== null) {
        this.experimentUpdateForm.controls[specGainField.key].setValue(
          `${specGainBaseValueA}${suffix}`,
        );
        this.experimentUpdateForm.controls[specGainField.key].markAsDirty();
      }
    });
  }

  override copyIntegrationTimeValues(): void {
    const integrationTimeValueA = this.f[this.integrationTimeAField.key]
      .value as number;
    const specGainValueA = this.f[this.specGainAField.key].value as
      | string
      | null;
    const specGainBaseValueA = specGainValueA?.slice(0, -1) ?? null;
    const targets: [FormBase<number>, FormBase<string>, string][] = [
      [this.integrationTimeBField, this.specGainBField, 'B'],
      [this.integrationTimeCField, this.specGainCField, 'C'],
      [this.integrationTimeDField, this.specGainDField, 'D'],
    ];
    targets.forEach(([integrationField, specGainField, suffix]) => {
      this.experimentUpdateForm.controls[integrationField.key].setValue(
        integrationTimeValueA,
      );
      this.experimentUpdateForm.controls[integrationField.key].markAsDirty();
      if (specGainBaseValueA !== null) {
        this.experimentUpdateForm.controls[specGainField.key].setValue(
          `${specGainBaseValueA}${suffix}`,
        );
        this.experimentUpdateForm.controls[specGainField.key].markAsDirty();
      }
    });
  }

  override copySpargingValues(): void {
    const typeValueA = this.f[this.spargingControlTypeAField.key].value as string;
    const baseTypeValue = typeValueA.slice(0, -1);
    const airflowValueA = this.f[this.airflowAField.key].value as number;
    const targets: [FormBase<string>, FormBase<number>, string][] = [
      [this.spargingControlTypeBField, this.airflowBField, 'B'],
      [this.spargingControlTypeCField, this.airflowCField, 'C'],
      [this.spargingControlTypeDField, this.airflowDField, 'D'],
    ];
    targets.forEach(([typeField, airflowField, suffix]) => {
      this.experimentUpdateForm.controls[typeField.key].setValue(
        `${baseTypeValue}${suffix}`,
      );
      this.experimentUpdateForm.controls[typeField.key].markAsDirty();
      this.experimentUpdateForm.controls[airflowField.key].setValue(
        airflowValueA,
      );
      this.experimentUpdateForm.controls[airflowField.key].markAsDirty();
    });
  }

  // ── Private overrides ──────────────────────────────────────────────────────

  protected override setFlaskControlState(
    flaskLetter: Flask,
    controlType:
      | 'temperature'
      | 'turbidostat'
      | 'chemostat'
      | 'ph'
      | 'ph2'
      | 'phChemo'
      | 'morbidostat'
      | 'sparging',
    enabled: boolean | null,
  ): void {
    const fieldMappings = {
      temperature: {
        A: [this.temperatureAField.key],
        B: [this.temperatureBField.key],
        C: [this.temperatureCField.key],
        D: [this.temperatureDField.key],
      },
      turbidostat: {
        A: [this.targetODAField.key],
        B: [this.targetODBField.key],
        C: [this.targetODCField.key],
        D: [this.targetODDField.key],
      },
      chemostat: {
        A: [this.flowRateAField.key],
        B: [this.flowRateBField.key],
        C: [this.flowRateCField.key],
        D: [this.flowRateDField.key],
      },
      ph: {
        A: [
          this.targetPHAField.key,
          this.inputTypeAField.key,
          this.pumpDurationAField.key,
        ],
        B: [
          this.targetPHBField.key,
          this.inputTypeBField.key,
          this.pumpDurationBField.key,
        ],
        C: [
          this.targetPHCField.key,
          this.inputTypeCField.key,
          this.pumpDurationCField.key,
        ],
        D: [
          this.targetPHDField.key,
          this.inputTypeDField.key,
          this.pumpDurationDField.key,
        ],
      },
      ph2: {
        A: [this.targetPH2AField.key, this.pumpDuration2AField.key],
        B: [this.targetPH2BField.key, this.pumpDuration2BField.key],
        C: [],
        D: [],
      },
      phChemo: {
        A: [
          this.targetPHChemoAField.key,
          this.pumpDurationChemoAField.key,
          this.inputTypeChemoAField.key,
          this.flowRatePHAField.key,
        ],
        B: [
          this.targetPHChemoBField.key,
          this.pumpDurationChemoBField.key,
          this.inputTypeChemoBField.key,
          this.flowRatePHBField.key,
        ],
        C: [],
        D: [],
      },
      morbidostat: {
        A: [
          this.thresholdODAField.key,
          this.dilutionPeriodAField.key,
          this.dilutionFactorAField.key,
        ],
        B: [
          this.thresholdODBField.key,
          this.dilutionPeriodBField.key,
          this.dilutionFactorBField.key,
        ],
        C: [
          this.thresholdODCField.key,
          this.dilutionPeriodCField.key,
          this.dilutionFactorCField.key,
        ],
        D: [
          this.thresholdODDField.key,
          this.dilutionPeriodDField.key,
          this.dilutionFactorDField.key,
        ],
      },
      sparging: {
        A: [this.airflowAField.key],
        B: [this.airflowBField.key],
        C: [this.airflowCField.key],
        D: [this.airflowDField.key],
      },
    };

    const fields = fieldMappings[controlType]?.[flaskLetter] || [];

    const index = Object.keys(fieldMappings[controlType]).indexOf(flaskLetter);

    const experimentValues = this.getExperimentValuesForControlType(
      controlType,
      flaskLetter,
    );

    const defaultValues = this.getDefaultValuesForControlType(controlType, flaskLetter);

    const isPHModule =
      controlType === 'ph' ||
      controlType === 'ph2' ||
      controlType === 'phChemo';
    const isMorbidostatControlType = controlType === 'morbidostat';

    fields.forEach((fieldKey: string, i: number) => {
      if (enabled && this.experiment.experimentStatusId === ExperimentStatus.Active) {
        this.f[fieldKey].enable();
      } else if (!enabled) {
        this.f[fieldKey].disable();
      }
      this.f[fieldKey].setValue(
        isPHModule || isMorbidostatControlType
          ? (experimentValues[i] ?? defaultValues[i])
          : (experimentValues[index] ?? defaultValues[index]),
      );
    });
  }

  protected override updateStirringSpeedState(
    flaskLetter: Flask,
    setToZero: boolean,
  ): void {
    const speedFieldMap = {
      A: this.speedAField,
      B: this.speedBField,
      C: this.speedCField,
      D: this.speedDField,
    };

    const speedField = speedFieldMap[flaskLetter];
    const fieldKey = speedField.key;
    const index = Object.keys(speedFieldMap).indexOf(flaskLetter);
    const defaultValues = this.getExperimentValuesForControlType(
      'stirringSpeed',
      flaskLetter,
    );

    if (setToZero) {
      this.f[fieldKey].setValue(0);
      this.f[fieldKey].disable();
    } else {
      this.f[fieldKey].setValue(
        this.experiment.speedRPMA === 0
          ? ExperimentParameterRanges.SPEED_RPM.default
          : defaultValues[index],
      );
      if (this.experiment.experimentStatusId === ExperimentStatus.Active) {
        this.f[fieldKey].enable();
      }
    }

    this.f[fieldKey].markAsDirty();
  }

  // ── Mode checking (reads directly from experiment object) ─────────────────

  protected override isExperimentMode(mode: ExperimentMode): boolean {
    return this.experiment.experimentMode === mode;
  }


  // ── Form value extraction helpers ──────────────────────────────────────────

  protected override getTurbidostatValues() {
    const getTurbidostatValue = (
      key: string,
      castType: 'boolean' | 'number' = 'boolean',
    ) => {
      const control = this.experimentUpdateForm.get(key);
      if (control && control.dirty) {
        return castType === 'boolean' ? !!control.value : Number(control.value);
      }
      return undefined;
    };
    return {
      maintainOD: undefined, // maintainOD isn't editable
      targetODA: getTurbidostatValue(
        this.targetODAField.key,
        'number',
      ) as number,
      targetODB: getTurbidostatValue(
        this.targetODBField.key,
        'number',
      ) as number,
      targetODC: getTurbidostatValue(
        this.targetODCField.key,
        'number',
      ) as number,
      targetODD: getTurbidostatValue(
        this.targetODDField.key,
        'number',
      ) as number,
      turbAActive: getTurbidostatValue(
        this.turbAActiveField.key,
        'boolean',
      ) as boolean,
      turbBActive: getTurbidostatValue(
        this.turbBActiveField.key,
        'boolean',
      ) as boolean,
      turbCActive: getTurbidostatValue(
        this.turbCActiveField.key,
        'boolean',
      ) as boolean,
      turbDActive: getTurbidostatValue(
        this.turbDActiveField.key,
        'boolean',
      ) as boolean,
    };
  }

  private getDefaultModuleValues() {
    const getValue = (
      key: string,
      castType: 'boolean' | 'number' = 'boolean',
    ) => {
      const control = this.experimentUpdateForm.get(key);
      if (control && control.dirty) {
        return castType === 'boolean' ? !!control.value : Number(control.value);
      }
      return undefined;
    };

    const temperatureControlA = this.f[this.temperatureControlAField.key].value;
    const temperatureControlB = this.f[this.temperatureControlBField.key].value;
    const temperatureControlC = this.f[this.temperatureControlCField.key].value;
    const temperatureControlD = this.f[this.temperatureControlDField.key].value;

    return {
      temperatureControlA: getValue(
        this.temperatureControlAField.key,
        'boolean',
      ) as boolean,
      temperatureControlB: getValue(
        this.temperatureControlBField.key,
        'boolean',
      ) as boolean,
      temperatureControlC: getValue(
        this.temperatureControlCField.key,
        'boolean',
      ) as boolean,
      temperatureControlD: getValue(
        this.temperatureControlDField.key,
        'boolean',
      ) as boolean,
      temperatureA: temperatureControlA === true
        ? (getValue(this.temperatureAField.key, 'number') as number)
        : null,
      temperatureB: temperatureControlB === true
        ? (getValue(this.temperatureBField.key, 'number') as number)
        : null,
      temperatureC: temperatureControlC === true
        ? (getValue(this.temperatureCField.key, 'number') as number)
        : null,
      temperatureD: temperatureControlD === true
        ? (getValue(this.temperatureDField.key, 'number') as number)
        : null,
      samplingTime: getValue(this.samplingTimeField.key, 'number') as number,
      experimentTime: getValue(
        this.experimentTimeField.key,
        'number',
      ) as number,
      speedA: getValue(this.speedAField.key, 'number') as number,
      speedB: getValue(this.speedBField.key, 'number') as number,
      speedC: getValue(this.speedCField.key, 'number') as number,
      speedD: getValue(this.speedDField.key, 'number') as number,
    };
  }

  protected override getChemostatValues() {
    const isChemostat = this.isChemostatMode();
    const isPHChemostat = this.isPHControlChemostatMode();

    const getChemostatValue = (
      key: string,
      castType: 'boolean' | 'number' = 'boolean',
    ) => {
      const control = this.experimentUpdateForm.get(key);
      if (control && control.dirty) {
        return castType === 'boolean' ? !!control.value : Number(control.value);
      }
      return undefined;
    };

    if (!isChemostat && !isPHChemostat) {
      return {
        chemoAActive: undefined,
        chemoBActive: undefined,
        chemoCActive: undefined,
        chemoDActive: undefined,
        flowRateA: undefined,
        flowRateB: undefined,
        flowRateC: undefined,
        flowRateD: undefined,
      };
    }

    return {
      chemoAActive: getChemostatValue(
        isChemostat
          ? this.chemoAActiveField.key
          : this.phChemoControlAField.key,
        'boolean',
      ) as boolean,
      chemoBActive: getChemostatValue(
        isChemostat
          ? this.chemoBActiveField.key
          : this.phChemoControlBField.key,
        'boolean',
      ) as boolean,
      chemoCActive: getChemostatValue(
        this.chemoCActiveField.key,
        'boolean',
      ) as boolean,
      chemoDActive: getChemostatValue(
        this.chemoDActiveField.key,
        'boolean',
      ) as boolean,
      flowRateA: getChemostatValue(
        isChemostat ? this.flowRateAField.key : this.flowRatePHAField.key,
        'number',
      ) as number,
      flowRateB: getChemostatValue(
        isChemostat ? this.flowRateBField.key : this.flowRatePHBField.key,
        'number',
      ) as number,
      flowRateC: getChemostatValue(this.flowRateCField.key, 'number') as number,
      flowRateD: getChemostatValue(this.flowRateDField.key, 'number') as number,
    };
  }

  protected override getPHControlValues() {
    const isPHControl = this.isPHControlMode();
    const isPHDouble = this.isPHControlDoubleMode();
    const isPHChemostat = this.isPHControlChemostatMode();

    if (!isPHControl && !isPHDouble && !isPHChemostat) {
      return {
        phControlA: undefined,
        phControlB: undefined,
        phControlC: undefined,
        phControlD: undefined,
        targetPHA: undefined,
        targetPHB: undefined,
        targetPHC: undefined,
        targetPHD: undefined,
        inputTypeA: undefined,
        inputTypeB: undefined,
        inputTypeC: undefined,
        inputTypeD: undefined,
        pumpDurationA: undefined,
        pumpDurationB: undefined,
        pumpDurationC: undefined,
        pumpDurationD: undefined,
      };
    }
    
    // Determine which fields to use based on mode
    const getControlField = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.phControlAField.key,
          B: this.phControlBField.key,
          C: this.phControlCField.key,
          D: this.phControlDField.key,
        };
        const hasBeenUpdated = this.experimentUpdateForm.get(
          fieldMap[flask],
        ).dirty;
        return hasBeenUpdated
          ? (this.f[fieldMap[flask]].value as boolean)
          : undefined;
      }
      if (isPHDouble && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.ph2ControlAField.key,
          B: this.ph2ControlBField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as boolean)
          : undefined;
      }
      return undefined;
    };

    const getTargetField = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.targetPHAField.key,
          B: this.targetPHBField.key,
          C: this.targetPHCField.key,
          D: this.targetPHDField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as number)
          : undefined;
      }
      if (isPHDouble && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.targetPH2AField.key,
          B: this.targetPH2BField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as number)
          : undefined;
      }
      if (isPHChemostat && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.targetPHChemoAField.key,
          B: this.targetPHChemoBField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as number)
          : undefined;
      }
      return undefined;
    };

    const getInputType = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.inputTypeAField.key,
          B: this.inputTypeBField.key,
          C: this.inputTypeCField.key,
          D: this.inputTypeDField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? convertInputTypeToBoolean(this.f[fieldMap[flask]].value as string)
          : undefined;
      }
      if (isPHChemostat && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.inputTypeChemoAField.key,
          B: this.inputTypeChemoBField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? convertInputTypeToBoolean(this.f[fieldMap[flask]].value as string)
          : undefined;
      }
      return undefined;
    };

    const getPumpDuration = (flask: Flask) => {
      if (isPHControl) {
        const fieldMap = {
          A: this.pumpDurationAField.key,
          B: this.pumpDurationBField.key,
          C: this.pumpDurationCField.key,
          D: this.pumpDurationDField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as number)
          : undefined;
      }
      if (isPHDouble && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.pumpDuration2AField.key,
          B: this.pumpDuration2BField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as number)
          : undefined;
      }
      if (isPHChemostat && (flask === 'A' || flask === 'B')) {
        const fieldMap = {
          A: this.pumpDurationChemoAField.key,
          B: this.pumpDurationChemoBField.key,
        };
        return this.experimentUpdateForm.get(fieldMap[flask]).dirty
          ? (this.f[fieldMap[flask]].value as number)
          : undefined;
      }
      return undefined;
    };

    return {
      phControlA: getControlField('A'),
      phControlB: getControlField('B'),
      phControlC: getControlField('C'),
      phControlD: getControlField('D'),
      targetPHA: getTargetField('A'),
      targetPHB: getTargetField('B'),
      targetPHC: getTargetField('C'),
      targetPHD: getTargetField('D'),
      inputTypeA: getInputType('A'),
      inputTypeB: getInputType('B'),
      inputTypeC: getInputType('C'),
      inputTypeD: getInputType('D'),
      pumpDurationA: getPumpDuration('A'),
      pumpDurationB: getPumpDuration('B'),
      pumpDurationC: getPumpDuration('C'),
      pumpDurationD: getPumpDuration('D'),
    };
  }

  protected override getFluorescenceValues() {
    const getFluorescenceValue = (
      key: string,
      castType: 'boolean' | 'number' = 'boolean',
      alwaysSubmitValue = false,
    ) => {
      const control = this.experimentUpdateForm.get(key);
      if ((control && control.dirty) || (control && alwaysSubmitValue)) {
        return castType === 'boolean' ? !!control.value : Number(control.value);
      }
      return undefined;
    };
    const getSpecGainValue = (key: string) => {
      const control = this.experimentUpdateForm.get(key);
      if (control && control.dirty) {
        return convertBooleanToSpecGain(control.value as string);
      }
      return undefined;
    };
    return {
      led1On: getFluorescenceValue('led1On', 'boolean', true) as boolean,
      led2On: getFluorescenceValue('led2On', 'boolean', true) as boolean,
      led3On: getFluorescenceValue('led3On', 'boolean', true) as boolean,
      led1Intensity: getFluorescenceValue(
        'led1Intensity',
        'number',
        true,
      ) as number,
      led2Intensity: getFluorescenceValue(
        'led2Intensity',
        'number',
        true,
      ) as number,
      led3Intensity: getFluorescenceValue(
        'led3Intensity',
        'number',
        true,
      ) as number,
      integrationTimeA: getFluorescenceValue(
        'integrationTimeA',
        'number',
      ) as number,
      integrationTimeB: getFluorescenceValue(
        'integrationTimeB',
        'number',
      ) as number,
      integrationTimeC: getFluorescenceValue(
        'integrationTimeC',
        'number',
      ) as number,
      integrationTimeD: getFluorescenceValue(
        'integrationTimeD',
        'number',
      ) as number,
      specGainA: getSpecGainValue('specGainA') as boolean,
      specGainB: getSpecGainValue('specGainB') as boolean,
      specGainC: getSpecGainValue('specGainC') as boolean,
      specGainD: getSpecGainValue('specGainD') as boolean,
      fluorescenceChannels: [],
    };
  }

  protected override getSpargingValues() {
    const getSpargingControlTypeValue = (key: string) => {
      const control = this.experimentUpdateForm.get(key);
      if (control && control.dirty) {
        return getSpargingStatus(control.value as string);
      }
      return undefined;
    };
    const getSpargingAirFlowValue = (
      airflowKey: string,
      controlTypeKey: string,
    ) => {
      const airflowControl = this.experimentUpdateForm.get(airflowKey);
      const controlTypeControl = this.experimentUpdateForm.get(controlTypeKey);
      if (
        airflowControl &&
        airflowControl.dirty &&
        controlTypeControl &&
        getSpargingStatus(controlTypeControl.value as string) !==
          SpargingStatus.Disabled
      ) {
        return airflowControl.value as number;
      }
      return undefined;
    };
    return {
      spargingControlTypeA: getSpargingControlTypeValue(
        this.spargingControlTypeAField.key,
      ),
      spargingControlTypeB: getSpargingControlTypeValue(
        this.spargingControlTypeBField.key,
      ),
      spargingControlTypeC: getSpargingControlTypeValue(
        this.spargingControlTypeCField.key,
      ),
      spargingControlTypeD: getSpargingControlTypeValue(
        this.spargingControlTypeDField.key,
      ),
      airFlowA: getSpargingAirFlowValue(
        this.airflowAField.key,
        this.spargingControlTypeAField.key,
      ),
      airFlowB: getSpargingAirFlowValue(
        this.airflowBField.key,
        this.spargingControlTypeBField.key,
      ),
      airFlowC: getSpargingAirFlowValue(
        this.airflowCField.key,
        this.spargingControlTypeCField.key,
      ),
      airFlowD: getSpargingAirFlowValue(
        this.airflowDField.key,
        this.spargingControlTypeDField.key,
      ),
    };
  }

  // ── Form initialisation helpers ────────────────────────────────────────────

//   protected override getBasicFormValues(
//     experimentData: Experiment | ExperimentUpdate | ExperimentTemplate | CreateExperiment,
//   ) {
//     return {
//       [this.experimentNameField.key]: experimentData.name,
//       [this.organismField.key]: experimentData.organism,
//       [this.strainField.key]: experimentData.strain,
//       [this.mediaField.key]: experimentData.media,
//       [this.noteField.key]:
//         'notes' in experimentData ? experimentData.notes : '',
//     };
//   }

//   private getFluorescenceFormValues(
//     experimentData: Experiment | ExperimentUpdate,
//   ) {
//     return {
//       // LED controls are always included (they're separate from fluorescence measurement)
//       [this.led1OnField.key]: experimentData.led1On,
//       [this.led2OnField.key]: experimentData.led2On,
//       [this.led3OnField.key]: experimentData.led3On,
//       [this.led1IntensityField.key]: experimentData.led1Intensity,
//       [this.led2IntensityField.key]: experimentData.led2Intensity,
//       [this.led3IntensityField.key]: experimentData.led3Intensity,
//       // Fluorescence measurement fields - only include if useFluorescence is true
//       ...(experimentData.useFluorescence
//         ? {
//             [this.integrationTimeAField.key]: experimentData.integrationTimeA,
//             [this.integrationTimeBField.key]: experimentData.integrationTimeB,
//             [this.integrationTimeCField.key]: experimentData.integrationTimeC,
//             [this.integrationTimeDField.key]: experimentData.integrationTimeD,
//             [this.specGainAField.key]: convertSpecGainToString(
//               experimentData.specGainA,
//               'A',
//             ),
//             [this.specGainBField.key]: convertSpecGainToString(
//               experimentData.specGainB,
//               'B',
//             ),
//             [this.specGainCField.key]: convertSpecGainToString(
//               experimentData.specGainC,
//               'C',
//             ),
//             [this.specGainDField.key]: convertSpecGainToString(
//               experimentData.specGainD,
//               'D',
//             ),
//           }
//         : {}),
//     };
//   }

//   private getOxygenFormValues(experimentData: Experiment | ExperimentUpdate) {
//     return {
//       [this.salinityAField.key]:
//         experimentData.salinityA ?? this.salinityAField.value,
//       [this.salinityBField.key]:
//         experimentData.salinityB ?? this.salinityBField.value,
//       [this.salinityCField.key]:
//         experimentData.salinityC ?? this.salinityCField.value,
//       [this.salinityDField.key]:
//         experimentData.salinityD ?? this.salinityDField.value,
//     };
//   }

//   private getSpargingFormValues(experimentData: Experiment | ExperimentUpdate) {
//     return {
//       [this.spargingControlTypeAField.key]: experimentData.spargingControlTypeA
//         ? convertSpargingStatus(experimentData.spargingControlTypeA as number) +
//           'A'
//         : this.spargingControlTypeAField.value,
//       [this.spargingControlTypeBField.key]: experimentData.spargingControlTypeB
//         ? convertSpargingStatus(experimentData.spargingControlTypeB as number) +
//           'B'
//         : this.spargingControlTypeBField.value,
//       [this.spargingControlTypeCField.key]: experimentData.spargingControlTypeC
//         ? convertSpargingStatus(experimentData.spargingControlTypeC as number) +
//           'C'
//         : this.spargingControlTypeCField.value,
//       [this.spargingControlTypeDField.key]: experimentData.spargingControlTypeD
//         ? convertSpargingStatus(experimentData.spargingControlTypeD as number) +
//           'D'
//         : this.spargingControlTypeDField.value,
//     };
//   }

}
